import React, { useState, useEffect, useRef } from "react";
import { Box, VStack, Text, Button, Spinner, Progress, HStack, Select, useColorModeValue, Center } from "@chakra-ui/react";
import QuestionBanner from "./components/QuestionBanner";

import axios from "axios";
import { ENDPOINTS } from "utils/Constants";
import { currentUser } from "utils/CurrentUser";
import { notify } from "components/ToastNotification";
import { getSocket } from "utils/SocketService";
import { useNavigate } from "react-router-dom";
import { decodeToken } from "utils/decodeToken";
import { USER_LEVELS } from "utils/Constants";


export default function HeadToHeadQuiz() {
    const navigate = useNavigate();
    const [quizStatus, setQuizStatus] = useState("selectSkill"); // "selectSkill", "waiting", "inProgress", "completed"
    const [selectedSkill, setSelectedSkill] = useState("");
    const [selectedLevel, setSelectedLevel] = useState(USER_LEVELS[0].charAt(0).toLowerCase() + USER_LEVELS[0].slice(1))
    const [selectedSkillName, setSelectedSkillName] = useState("");
    const [skills, setSkills] = useState([]);
    const [question, setQuestion] = useState(null);
    const [quizId, setQuizID] = useState(null);
    const [currentOpponent, setOpponent] = useState(null);
    const [currentUserScore, setCurrentUserScore] = useState(0);
    const [opponentScore, setOpponentScore] = useState(0);
    const [timeLeft, setTimeLeft] = useState(30);
    const [answered, setAnswered] = useState(false);
    const [questionIndex, setQuestionIndex] = useState(0);
    const [socket, setSocket] = useState(null);
    const [isAvailable, setIsAvailable] = useState(false)
    const [decodedToken, setDecodedToken] = useState({});

    const contentBg = useColorModeValue("white", "gray.700");

    const handleQuizStarted = (data) => {
        const { opponent, question, quizId } = data.data;
        setOpponent(opponent);
        setQuestion(question);
        setQuizID(quizId);
        setTimeLeft(30);
        setAnswered(false);
        setQuestionIndex(0);
        setQuizStatus("inProgress");
    };

    const handleNewQuestion = (data) => {
        setTimeout(() => {
            const scores = data.data.scores;
            setQuestion(data.data.question);
            setTimeLeft(30);
            setAnswered(false);
            setQuestionIndex((prevIndex) => prevIndex + 1);
            setCurrentUserScore(scores[currentUser.id]);
            setOpponentScore(scores[opponentRef.current.id]);
        }, 1000)
    };

    const handleOpponentScoreUpdate = (score) => {
        setOpponentScore(score);
    };

    const handleQuizCompleted = (data) => {
        const scores = data.data.scores;
        setQuizStatus("completed");
        setCurrentUserScore(scores[currentUser.id]);
        setOpponentScore(scores[opponentRef.current.id]);
    };

    const handleOpponentAnswered = (response) => {
        if (response.success) {
            if (response.data.currentUser != currentUser.id) {
                notify("Opponent has answered!");
            }
        }
    };

    const handleUserQuitTheQuiz = (response) => {
        notify("Opponent has left the quiz!");
    };

    const opponentRef = useRef(null);

    useEffect(() => {
        opponentRef.current = currentOpponent;
    }, [currentOpponent]);

    useEffect(() => {
        const token = decodeToken();

        if (token?.pricingPlanId === 3) {
            setIsAvailable(true);
        } else {
            setIsAvailable(false);
        }
    }, [currentUser.token]);


    useEffect(() => {
        const socketInstance = getSocket();
        setSocket(socketInstance);

        socketInstance.on("quizStarted", handleQuizStarted);
        socketInstance.on("question", handleNewQuestion);
        socketInstance.on("answerReceived", handleOpponentAnswered);
        socketInstance.on("opponentScoreUpdate", handleOpponentScoreUpdate);
        socketInstance.on("userQuit", handleUserQuitTheQuiz);
        socketInstance.on("quizCompleted", handleQuizCompleted);

        const fetchSkills = async () => {
            try {
                const response = await axios.get(ENDPOINTS.GET_SKILLS_FOR_H2HQUIZ, {
                    headers: { Authorization: `Bearer ${currentUser.token}` },
                });
                localStorage.setItem('token', response.data.data.token);
                setSkills(response.data.data.skills);
            } catch (error) {
                notify("Unable to fetch skills", "error");
            }
        };

        fetchSkills();

        return () => {
            socketInstance.disconnect();
        };
    }, []);

    useEffect(() => {
        if (quizStatus === "inProgress" && timeLeft > 0) {
            const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
            return () => clearTimeout(timer);
        } else if (timeLeft === 0 && quizStatus === "inProgress") {
            !answered && socket.emit("timeUp", { quizId, token: currentUser.token, questionIndex });
        }
    }, [timeLeft, quizStatus, socket]);

    const handleAnswerClick = (answer) => {
        if (answered) return;
        setAnswered(true);
        const timeTaken = 30 - timeLeft;
        socket?.emit("submitAnswer", { quizId, token: currentUser.token, answer, questionIndex, timeTaken: timeTaken });
    };

    const handleSkillSelect = () => {
        if (selectedSkill) {
            setSelectedSkillName(skills.find(skill => skill.id == selectedSkill).name);
            setQuizStatus("waiting");
            socket?.emit("startHeadToHeadQuiz", { token: currentUser.token, skillId: selectedSkill, selectedLevel });
        }
    };

    const handleSkillChange = (target) => {
        const currentLevel = target.options[target.selectedIndex].getAttribute("data-level");
        const selectedSkillId = target.value;
        setSelectedSkill(selectedSkillId)
        setSelectedLevel(currentLevel);
    }

    const renderSkillSelection = () => (
        <VStack align="center" justify="center" h="100vh" spacing={6} bg={contentBg}>
            <Text fontSize="xl" fontWeight="bold">Select a Skill to Start</Text>
            <Select
                placeholder="Select Skill"
                onChange={(e) => handleSkillChange(e.target)}
                value={selectedSkill}
                w="300px"
            >
                {skills.map((skill) => (
                    <option key={skill.id + skill.level} value={skill.id} data-level={skill.level}>
                        {skill.name} - ({skill.level ? skill.level[0].toUpperCase() + skill.level.slice(1) : ''})
                    </option>
                ))}
            </Select>
            <Button
                variant="darkBrand"
                onClick={handleSkillSelect}
                isDisabled={!selectedSkill}
            >
                Start Quiz
            </Button>
        </VStack>
    );

    const renderWaitingState = () => (
        <VStack align="center" justify="center" h="100vh" bg={contentBg}>
            <Spinner size="xl" />
            <Text fontSize="lg" mt={4}>
                Waiting for an opponent for <b>{selectedSkillName || "the selected skill"}</b>...
            </Text>
        </VStack>
    );

    const renderQuizInProgress = () => (
        <Box pt={{ base: "180px", md: "80px", xl: "80px" }} bg={contentBg}>
            <HStack justify="space-between" mb={4}>
                <VStack>
                    <Text fontSize="lg" fontWeight="bold">You</Text>
                    <Text>Score: {currentUserScore}</Text>
                </VStack>
                <VStack>
                    <Text fontSize="lg" fontWeight="bold">{`@${opponentRef.current?.username}` || "Opponent"}</Text>
                    <Text>Score: {opponentScore}</Text>
                </VStack>
            </HStack>
            <Text textAlign="center" fontSize="lg" fontWeight="bold" color="red.500" mb={4}>
                Time Left: {timeLeft}s
            </Text>
            {/* <Box
                p={4}
                // bg="gray.100"
                borderRadius="lg"
                boxShadow="sm"
                mb={4}
                textAlign="center"
            > */}
            <QuestionBanner question={questionIndex + 1 + '. ' + question?.question} />
            {/* <Text fontSize="xl" fontWeight="bold">{questionIndex + 1}. {question?.question}</Text> */}
            {/* </Box> */}
            <VStack mt={'2%'} spacing={4}>
                {question.answers.map((option, index) => (
                    <Button
                        key={index}
                        w="100%"
                        colorScheme={(answered && option.isCorrect ? "green" : (answered && !option.isCorrect ? "red" : "gray"))}
                        onClick={() => handleAnswerClick(option.text)}
                        isDisabled={answered}
                    >
                        {option.text}
                    </Button>
                ))}
            </VStack>
        </Box>
    );

    const renderQuizCompleted = () => {
        const winner = currentUserScore > opponentScore
            ? "You Win!"
            : currentUserScore < opponentScore
                ? `@${opponentRef.current?.username || "Opponent"} Wins!`
                : "It's a Tie!";
        return (
            <VStack align="center" justify="center" h="100vh" spacing={6} bg={contentBg}>
                <Text fontSize="2xl" fontWeight="bold">{winner}</Text>
                <Text fontSize="lg">Your Score: {currentUserScore}</Text>
                <Text fontSize="lg">Opponent's Score: {opponentScore}</Text>
                <Button colorScheme="teal" onClick={() => window.location.reload()}>
                    Play Again
                </Button>
            </VStack>
        );
    };

    const renderNotAvailable = () => (
        <Center h="100vh" bg={contentBg}>
            <VStack spacing={6}>
                <Text fontSize="2xl" fontWeight="bold" color="red.500">
                    This feature is not available for your pricing plan.
                </Text>
                <Text fontSize="lg" color="gray.500" textAlign="center">
                    Upgrade to <b>Premium Plan</b> to access the Head-to-Head Quiz.
                </Text>
                <Button
                    variant="darkBrand"
                    onClick={() => navigate('/admin/profile')}
                >
                    Upgrade Now
                </Button>
            </VStack>
        </Center>
    );

    if (!isAvailable) return renderNotAvailable();

    if (quizStatus === "selectSkill") return renderSkillSelection();
    if (quizStatus === "waiting") return renderWaitingState();
    if (quizStatus === "inProgress") return renderQuizInProgress();
    if (quizStatus === "completed") return renderQuizCompleted();

    return null;
}
